import { useEffect, useState, useCallback } from 'react';
import { getAnnouncements, deleteAnnouncement } from 'api/announcement';
import { makeAnnouncementsFormat, filterAnnouncements } from 'containers/pages/announcementManagement/libs';
import { useAnnouncementAdminAccess } from 'containers/pages/announcementManagement/hooks';
import { useNotificationProvider } from 'hooks';
import { Announcement } from 'containers/pages/announcementManagement/types';

function useAnnouncementManagement() {
  const { notifySuccess, notifyError } = useNotificationProvider();
  useAnnouncementAdminAccess();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [searchedAnnouncements, setSearchedAnnouncements] = useState<Announcement[]>([]);
  const [searchInput, setSearchInput] = useState('');

  // Toggle add form open/close
  const onToggleForm = () => {
    setIsOpenForm((prev) => !prev);
  };

  // Search handlers
  const onSearchInput = (input: string) => {
    setSearchInput(input);
    if (!input) {
      setSearchedAnnouncements(announcements);
    } else {
      const filtered = filterAnnouncements(input, announcements);
      setSearchedAnnouncements(filtered);
    }
  };

  const onResetSearch = () => {
    setSearchInput('');
    setSearchedAnnouncements(announcements);
  };

  // Delete handler
  const onDeleteAnnouncement = async (id: number) => {
    try {
      await deleteAnnouncement(id);
      notifySuccess('Announcement deleted successfully');
      await onRefreshAnnouncements();
    } catch (error) {
      console.error('[useAnnouncementManagement] Delete failed:', error);
      notifyError('Failed to delete announcement. Please try again.');
      throw error;
    }
  };

  // Refresh announcement list
  const onRefreshAnnouncements = useCallback(async () => {
    try {
      const result = await getAnnouncements();
      const formatted = makeAnnouncementsFormat(result);
      setAnnouncements(formatted);
      setSearchedAnnouncements(formatted);
      setSearchInput('');
      setIsOpenForm(false);
    } catch (error) {
      console.error('[useAnnouncementManagement] Refresh failed:', error);
      notifyError('Failed to refresh announcements');
    }
  }, [notifyError]);

  // Initial data load
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getAnnouncements();
        const formatted = makeAnnouncementsFormat(result);
        setAnnouncements(formatted);
        setSearchedAnnouncements(formatted);
      } catch (error) {
        console.error('[useAnnouncementManagement] Initial load failed:', error);
        notifyError('Failed to load announcements');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notifyError]);

  const state = {
    isLoading,
    isOpenForm,
    searchedAnnouncements,
    searchInput,
  };

  const handler = {
    onToggleForm,
    onSearchInput,
    onResetSearch,
    onDeleteAnnouncement,
    onRefreshAnnouncements,
  };

  return { state, handler };
}

export default useAnnouncementManagement;
